/** Signed-in user fields for the session. Page data has its own queries; keep the session small. */
export const USER_FRAGMENT = (): string =>
  `fragment User on User {
 __typename
 ... on SuperAdmin{
    id
    email
    name
    adminRole:role
    phone
}
 ... on Advisor{
    id
    firstName
    lastName
    company
    title
    advisorPhone:phone
    email
}
... on Client{
    id
    firstName
    lastName
    phone
    email
    workEmail
    role
    invited
    preferredLanguage
}
}
`;

export const LIST_TIERS_QUERY = `query {listTiers{
    id
    name
    description
    code
    stripeProductId
    entitlements {
        id
        isIncluded
        type
        feature {
            id
            name
            featureFlagKey
            description
        }
    }
}}
`;

export const SESSION_QUERY = (userFragment: string): string => `query{
  currentSession{
    user{
      ...User  
    }
    auth{
    token
    }
  }
}
${userFragment}
`;

export const SUPERADMIN_ORGANIZATION_QUERY = `
query ($id: ID!) {
  superAdminOrganization(id: $id) {
    id
    name
    manageOrgToken
    status
    createdAt
    logoUrl
    isPayingCustomer
    clientsCount
    orgLogs {
      operationName
      actionLabel
      superadmin {
        id
        name
      }
      createdAt
    }
    currentTier {
      id
      name
      description
      code
    }
    organizationFeatures {
      id
      feature {
        id
        name
        featureFlagKey
        description
      }
      type
      enabledAt
      disabledAt
    }
  }
}`;

/** First client with the Admin role, for the admin email shown on billing drawers. */
export const SUPERADMIN_ORGANIZATION_ADMIN_CLIENT_QUERY = `
query ($orgId: ID!) {
  superAdminOrganizationClients(orgId: $orgId, limit: 1, filter: { role: Admin }) {
    items {
      id
      email
    }
  }
}`;

export const SUPERADMIN_ORGANIZATION_CLIENTS_QUERY = `
query ($orgId: ID!, $offset: Int, $limit: Int, $filter: OrganizationClientsFilter) {
  superAdminOrganizationClients(orgId: $orgId, offset: $offset, limit: $limit, filter: $filter) {
    totalCount
    items {
      id
      firstName
      lastName
      email
      workEmail
      phone
      role
      invitationStatus
      onboardingStatus
      preferredLanguage
    }
  }
}`;

export const SUPERADMIN_ADVISORS_QUERY = `query {
  superAdminAdvisors {
    id
    firstName
    lastName
    company
    title
    phone
    email
    bookingUrl
    preferredLanguage
  }
}`;

export const SUPERADMIN_ORGANIZATIONS_QUERY = `query {
superAdminOrganizations {
    id
    name
    currentTier {
        id
        name
        description
        code
        }
    organizationFeatures {
        id
        feature {
            id
            name
            featureFlagKey
            description
        }
        type
        enabledAt
        disabledAt
    }
    organizationBilling {
      purchasedSeats
      status
      billingModel
      startedAt
      endsAt
      billingInterval
      usedSeats
      nextBillingDate
    }
    admin{
        id
        name
        email
    }
    advisor{
        id
        firstName
        lastName
        company
        title
        phone
        email
    }
    status
    clientsCount
    isTcrOnly
    isPayingCustomer
}
}`;

export const ALL_ADMINS_QUERY = `query{
    allAdmins{
        id
        email
        role
        name
    }
}`;

export const SUPERADMIN_SETTINGS_QUERY = (settingsFragment: string): string => `query {
  superAdminSettings {
    ...settings
  }
}
${settingsFragment}
`;
export const SETTINGS_FRAGMENT = `
    fragment settings on Settings {
    tax {
        cppIncomeThreshold
        cppCredit {
        ...taxCredit
        }
        eiCredit {
        ...taxCredit
        }
        personalCredit
        refundableTaxClaim
        canadaEmploymentAmount
        federal {
        ...taxSlab
        }
        ab {
        ...taxSlab
        }
        bc {
        ...taxSlab
        }
        mb {
        ...taxSlab
        }
        nb {
        ...taxSlab
        }
        nl {
        ...taxSlab
        }
        ns {
        ...taxSlab
        }
        nt {
        ...taxSlab
        }
        nu {
        ...taxSlab
        }
        on {
        ...taxSlab
        }
        pe {
        ...taxSlab
        }
        qc {
        ...taxSlab
        }
        sk {
        ...taxSlab
        }
        yt {
        ...taxSlab
        }
    }
    emergencySavings {
        interestRate
    }
    insurance {
        life {
            ...settingsInsuranceLife
        }
    disability {
        incomeCoveragePercent
        }
    }
    
    expenses{
        thresholds {
        ...settingsExpensesThresholds
        }
    }
    
    debt {
        refinancingMortgage {
        ...settingsDebtInterest
        }
        refinancingByAllDebts {
        ...settingsDebtInterest
        }
        refinancingWithoutMortgage {
        ...settingsDebtInterest
        }
        mortgageQualification {
        ...settingsDebtMortageQualification
        }
    }
    
    retirement {
      appreciationRate
      inflationRate
      incomeRequiredAtRetirement
      definedContributionMaxLimit
      groupRrspContributionMaxLimit
      rrspContributionMaxLimit
      tfsaContributionMaxLimit
      maxCppPayout
      maxOasPayout
      oasStartingAge
    }

    riskTolerance {
      growth
      moderateGrowth
      moderate
      conservativeGrowth
      conservative
    }
}


fragment taxCredit on TaxCredit {
    threshold
    contributionRate
}

fragment taxSlab on TaxSlab {
    limit
    rate
}

fragment settingsInsuranceLife on SettingsInsuranceLife {
    inflationRate
    incomePercentToReplace
    canadaPensionPlanPayout
    funeralExpenses
    survivorBenefitsUnder65
    survivorBenefitsAbove65
    childrensBenefits
}

fragment settingsExpensesThresholds on SettingsExpensesThresholds {
    transportationTotal
    recreationTotal
    housingTotal
    housingUtilities
    personalCareClothing
    personalCareMedical
    personalCareGroceries
}

fragment settingsDebtInterest on SettingsDebtInterest {
    interestRate
    totalYearsToPayDebt
}

fragment settingsDebtMortageQualification on SettingsDebtMortgageQualification {
    downPayment
    grossDebtServiceRatio
    interestRate
    propertyInsurance
    totalDebtServiceRatio
    totalYearsToPayDebt
}
`;

export const ALL_PENDING_USERS_BY_ORG_REQUESTS_QUERY = `
    query($orgId: ID!){
        allPendingUsersByOrg(orgId: $orgId){
            id
            email
            firstName
            lastName
            org {
                name
            }
        }
    }
`;

export const LOOKUP_USER_BY_EMAIL_QUERY = `
    query($email: String!){
        lookupUserByEmail(email: $email){
            id
            email
            workEmail
            firstName
            lastName
            phone
            role
            invitationStatus
            onboardingStatus
            preferredLanguage
            org {
                id
                name
                status
            }
        }
    }
`;

export const GET_ORG_IDS_BY_FEATURE_FLAGS_CONFIG = `query getOrgIdsByFeatureFlagConfig {
	getOrgIdsByFeatureFlagConfig {
		featureFlagName
		orgIds
	}
}`;

export const GET_INVOICES = `
  query($orgId: ID!, $limit: Int) {
      getInvoicesByOrgId(orgId: $orgId, limit: $limit) {
          invoices {
            ...invoiceFields
          }
          hasMore
          totalCount
      }
  }

  fragment invoiceFields on Invoice {
    id
    number
    status
    amountPaid
    amountDue
    amountRemaining
    currency
    description
    invoicePdf
    hostedInvoiceUrl
    editInvoiceUrl
    periodStart
    periodEnd
    dueDate
    createdAt
    paidAt
    subscriptionId
    customerId
    attempted
    attemptCount
    lines {
      id
      description
      quantity
      product
    }
    paymentIntentId
  }
`;

export const GET_ORGANIZATION_BILLING = `
  query($orgId: ID!) {
    getOrganizationBillingByOrgId(orgId: $orgId) {
      ...organizationBillingFields
    }
  }

  fragment organizationBillingFields on OrganizationBilling {
    id
    provider
    status
    purchasedSeats
    startedAt
    endsAt
    interval
    usedSeats
    billingInterval
    billingModel
    nextBillingDate
    trialEndsAt
    trialPeriodDays
    purchasedTier {
      id
      name
      description
      code
    }
    addons {
      id
      name
      featureFlagKey
      description
    
    }
    paymentMethod {
      id
      type
      last4
      brand
      expMonth
      expYear
    }
    subscriptionDetails {
      amount
      interval
      status
      breakdown {
        amount
        name
        id
        type
      }
    }
    discount {
      promotionCodeId
      couponId
      code
      couponName
      percentOff
      amountOff
    }
  }
`;

export const GET_PAYMENT_INTENT = `
  query($paymentIntentId: ID!) {
    getPaymentIntent(paymentIntentId: $paymentIntentId) {
      ...paymentIntentFields
    }
  }

  fragment paymentIntentFields on PaymentIntent {
    id
    created
    status
    amount
    currency
    paymentMethod {
      id
      type
      last4
      brand
      expMonth
      expYear
      funding
    }
    error {
      code
      docUrl
      type
      declineCode
      message
    }
  }
`;

export const LIST_DISCOUNT_CODES_QUERY = `
  query {
    listDiscountCodes {
      discountCodes {
        id
        name
        code
        discountPercentage
        maxRedemptions
        timesRedeemed
        active
      }
    }
  }
`;

export const GET_ORGANIZATION_PREVIEW_MODE_STATUS_QUERY = `
query ($organizationId: ID!) {
    getOrganizationPreviewModeStatus(organizationId: $organizationId)
}`;

export const GET_ORGANIZATION_DEMO_EMPLOYEES_STATUS_QUERY = `
query ($organizationId: ID!) {
    getOrganizationDemoEmployeesStatus(organizationId: $organizationId)
}`;

export const GET_ORGANIZATION_ANALYTICS_EVENTS_STATUS_QUERY = `
query ($organizationId: ID!) {
    getOrganizationAnalyticsEventsStatus(organizationId: $organizationId)
}`;

export const GET_ORG_CUSTOM_PRICES_FRAGMENT = `
fragment customPriceFields on CustomRateReference {
  id
  productType
  tierId
  featureId
  amount
  }
`;

export const GET_ORG_CUSTOM_PRICES = `
  query($orgId: ID!) {
    getOrgCustomPrices(orgId: $orgId) {
      ...customPriceFields
    }
  }

  ${GET_ORG_CUSTOM_PRICES_FRAGMENT}
`;

export const GLOBAL_ACTIVITY_LOG_QUERY = `query (
  $filter: ActivityLogFilterInput!
  $sort: ActivityLogSortInput
  $pagination: ActivityLogPaginationInput
) {
  globalActivityLog(filter: $filter, sort: $sort, pagination: $pagination) {
    total
    page
    limit
    totalPages
    items {
      id
      createdAt
      action
      actionLabel
      administrator { id name email deleted }
      organization { id name deleted }
      target { type id label }
    }
  }
}`;

export const ACTIVITY_LOG_FILTER_OPTIONS_QUERY = `query {
  activityLogFilterOptions {
    organizations { id name deleted }
    administrators { id name email deleted }
    actions { name label }
  }
}`;
